import environment from 'platform/utilities/environment';
import { datadogRum } from '@datadog/browser-rum';
import { DATA_FILES_PATH, FETCH_TIMEOUT_MS } from '../constants';

/**
 * Records a static data fetch failure to Datadog RUM.
 *
 * @param {string} fileName - The file that failed to load.
 * @param {Error} error - The error that occurred.
 * @returns {Error} - The same error, so callers can report-and-return in one step.
 */
const reportFetchError = (fileName, error) => {
  // datadogRum.addError is a no-op when RUM isn't initialized, so this is safe
  // to call unconditionally.
  datadogRum?.addError?.(error, {
    feature: 'drupal-static-data',
    fileName,
  });

  return error;
};

/**
 * Resolves the base URL for a static data file.
 *
 * Static data files (e.g. va-healthcare-services.json, vamc-ehr.json) are
 * emitted into content-build's build output under /data/cms/. In real envs
 * (dev/staging/prod) they're served from BASE_URL (the FE host). When
 * BUILDTYPE is `localhost`, the page is served by whatever is running on the
 * same origin — either webpack-dev-server on `localhost:3001` during native
 * local dev, or content-build at `<hash>.vfs.va.gov` on a review instance. In
 * both cases the correct host is the page's own origin, NOT
 * `environment.API_URL` (vets-api), which doesn't serve `/data/cms/*`.
 * Note: 127.0.0.1 is included for Node 22+ compatibility where DNS resolution
 * may prefer IPv6.
 */
const resolveBaseUrl = (server, useLocalOverride) => {
  if (server) {
    return server;
  }
  const isLocal =
    useLocalOverride &&
    (environment.isLocalhost?.() ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  return isLocal ? window.location.origin : environment.BASE_URL;
};

/**
 * Fetches a Drupal static data file from the appropriate server.
 *
 * @param {string} fileName - The name of the file to fetch.
 * @param {string} [server] - Optional custom server URL to override the default base URL.
 * @param {boolean} [useLocalOverride=true] - Switch to enable or disable local environment check.
 * @param {number} [timeout=FETCH_TIMEOUT_MS] - Milliseconds to wait before aborting the fetch.
 * @returns {Promise<Object|Error>} - The parsed JSON response, or an Error if the fetch fails.
 */
export const fetchDrupalStaticDataFile = async ({
  fileName,
  server,
  useLocalOverride = true,
  timeout = FETCH_TIMEOUT_MS,
}) => {
  const url = `${resolveBaseUrl(
    server,
    useLocalOverride,
  )}/${DATA_FILES_PATH}/${fileName}`;

  // Abort the request if it hangs past the timeout so callers aren't left
  // waiting indefinitely on a stalled connection.
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), timeout)
    : undefined;

  try {
    const result = await fetch(url, { signal: controller?.signal });

    if (result?.ok) {
      return await result.json();
    }

    const { status = 'unknown', statusText = '' } = result ?? {};
    return reportFetchError(
      fileName,
      new Error(`Unexpected response (${status} ${statusText})`.trim()),
    );
  } catch (err) {
    return reportFetchError(
      fileName,
      err instanceof Error ? err : new Error(String(err)),
    );
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
